import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ExportEntityTemplate,
  getEntityDetails,
} from "../../services/entitiesService";
import { Logger, toastActions } from "@base-provider";
import { FileHandler } from "./index";
import { fileUploadActions } from "../../redux/reducers/fileUploadReducer";
//core imports
import SFCard, { SFCardBody, SFCardHeader } from "../../../components/card";
import SFTypography from "../../../components/typography";
import SFButton from "../../../components/button";
import { SFGridItem, SFGridContainer, SFStack } from "../../../components/grid";
import { SFDownloadIcon } from "../../../components/icons";
import SFHaloIconWrapper from "../../../components/icons/halo";
import getStyled from "../../../hooks/getStyled";
import SFUpload from "../../../components/fileUploader";
import SFDialog from "../../../components/dialog";

const { styled } = getStyled();

const StyledTemplateCaption = styled(SFTypography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const UploadFileStep = forwardRef((props, ref) => {
  const [uploadFileState, setUploadFileState] = useState({
    entity: props.entity,
    schema: null,
    expectedFields: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const reduxState = useSelector((state) => state.fileUpload);

  const dispatch = useDispatch();

  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    onStepChange: stepChangeHandler,
    onLoad: loadStepHandler,
  }));

  const stepChangeHandler = async (data) => {
    Logger.of("App.uploadFile.uploadFileStep.changeStepHandler").info({ data });
    if (data.nextStep < data.activeStep) {
      return true;
    }
    if (FileHandler.file) {
      return true;
    } else {
      setIsModalOpen(true);
      return false;
    }
  };

  const fileSelectHandler = async (file) => {
    Logger.of("App.uploadFile.uploadFileStep.fileSelectHandler").info({
      data: file,
    });

    if (file) {
      await FileHandler.setFile(file, uploadFileState.schema);
      if (FileHandler.sheetCount > 1) {
        fileRemoveHandler();
        dispatch(
          toastActions.showErrorMessage(
            t("fileUploadWizard.multipleSheetsError")
          )
        );
        return false;
      }
      if (FileHandler.fileData.length === 0) {
        fileRemoveHandler();
        dispatch(
          toastActions.showErrorMessage(t("fileUploadWizard.emptyFileError"))
        );
        return false;
      }
      dispatch(
        fileUploadActions.setInitialData({
          schema: uploadFileState.schema,
          columnNames: Object.keys(FileHandler.fileData[0]),
          fileData: FileHandler.fileData,
          fileName: FileHandler.getFileName(),
          fileSize: FileHandler.getFileSizeString(),
          t,
        })
      );
      return true;
    }
  };

  const fileRemoveHandler = () => {
    FileHandler.clearFile();
    dispatch(fileUploadActions.resetData());
  };

  const getEntityConfiguration = (entityId) => {
    getEntityDetails(entityId).then((res) => {
      const schemaJson = JSON.parse(res.data.configurationDefinition.schema);
      Logger.of("App.uploadFile.matchDataStep.getEntityDetails").info({
        response: res.data,
        schemaJson,
      });
      setUploadFileState((curState) => ({
        ...curState,
        entity: res.data,
        schema: schemaJson,
      }));
    });
  };

  const loadStepHandler = (stepperData) => {
    Logger.of("App.uploadFile.uploadFileStep.loadStepHandler").info({
      data: stepperData,
    });

    getEntityConfiguration(
      stepperData.data[0].data?.entity?.entity?.id ?? props.entity.id
    );
  };

  const closeDialogHandler = () => {
    setIsModalOpen(false);
  };

  return (
    <React.Fragment>
      <SFDialog
        variant="error"
        title={t("fileUploadWizard.fileUploadStep.dialog.header")}
        message={t("fileUploadWizard.fileUploadStep.dialog.body")}
        open={isModalOpen}
        onClose={closeDialogHandler}
        primaryButton
        primaryButtonProps={{
          onClick: closeDialogHandler,
        }}
      />
      <SFGridContainer>
        <SFGridItem xs={7}>
          <SFCard>
            <SFCardHeader
              title={
                <SFTypography variant="h3">
                  {t("fileUploadWizard.fileUploadStep.mainHeader", {
                    name:
                      props?.name.toLowerCase() ??
                      uploadFileState?.entity?.name.toLowerCase(),
                  })}
                </SFTypography>
              }
            ></SFCardHeader>
            <SFCardBody>
              <SFUpload
                acceptedFileTypes={props.acceptedFileTypes}
                fileName={reduxState.file.name}
                fileSize={reduxState.file.size}
                fileTypeName={uploadFileState?.entity?.name}
                onFileSelected={fileSelectHandler}
                onFileRemoved={fileRemoveHandler}
              />
            </SFCardBody>
          </SFCard>
        </SFGridItem>
        <SFGridItem xs={5}>
          <SFCard>
            <SFCardBody>
              <SFStack direction="row" spacing={3}>
                <div>
                  <SFHaloIconWrapper variant="grey">
                    <SFDownloadIcon />
                  </SFHaloIconWrapper>
                </div>
                <div>
                  <SFTypography variant="body1bold">
                    {t("fileUploadWizard.fileUploadStep.templateHeader", {
                      name: props?.name ?? uploadFileState?.entity?.name,
                    })}
                  </SFTypography>
                  <StyledTemplateCaption variant="body1bold" component="div">
                    {t("fileUploadWizard.fileUploadStep.templateCaption", {
                      name:
                        props?.name.toLowerCase() ??
                        uploadFileState?.entity?.name.toLowerCase(),
                    })}
                  </StyledTemplateCaption>
                  <div>
                    <SFButton
                      onClick={async () =>
                        await ExportEntityTemplate(
                          uploadFileState?.entity?.id,
                          uploadFileState?.entity?.name,
                          props.templateName
                        )
                      }
                      variant="secondary"
                    >
                      {t("fileUploadWizard.fileUploadStep.downloadTemplate")}
                    </SFButton>
                  </div>
                </div>
              </SFStack>
            </SFCardBody>
          </SFCard>
        </SFGridItem>
      </SFGridContainer>
    </React.Fragment>
  );
});

export default UploadFileStep;
